/**
 * Service Keycloak OAuth2 pour l'authentification du dashboard
 * Utilise Authorization Code Flow avec PKCE (S256)
 */

import {
  generateCodeVerifier,
  generateCodeChallenge,
  parseJwt,
  isTokenExpired,
  extractUserFromToken,
} from '@/utils/pkce';

// Configuration Keycloak (peut être surchargée par variables d'environnement)
const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || 'https://keycloak-uat.filmika.io';
const REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'filmika';
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'filmika-dashboard';
const REDIRECT_URI = import.meta.env.VITE_KEYCLOAK_REDIRECT_URI || `${window.location.origin}/auth/callback`;

// Endpoints Keycloak
const AUTH_ENDPOINT = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/auth`;
const TOKEN_ENDPOINT = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/token`;
const LOGOUT_ENDPOINT = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/logout`;

// Clés localStorage
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refresh_token',
  ID_TOKEN: 'id_token',
  CODE_VERIFIER: 'code_verifier',
  USER: 'user',
} as const;

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
  refresh_expires_in?: number;
  token_type: string;
  scope: string;
}

export interface KeycloakUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  emailVerified?: boolean;
}

/**
 * Redirige vers la page de login Keycloak
 */
export async function initiateLogin(): Promise<void> {
  // Générer PKCE
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Stocker le verifier pour l'échange de code
  sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);

  // Construire l'URL d'autorisation
  const authUrl = new URL(AUTH_ENDPOINT);
  authUrl.searchParams.set('client_id', CLIENT_ID);
  authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
  authUrl.searchParams.set('response_type', 'code');
  // Pas d'offline_access pour le backoffice : les admins ne doivent pas obtenir
  // de token longue durée (le client backoffice-spa n'a pas ce scope assigné,
  // le demander provoquerait une erreur invalid_scope de Keycloak).
  authUrl.searchParams.set('scope', 'openid profile email roles');
  authUrl.searchParams.set('code_challenge', codeChallenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');

  // Rediriger vers Keycloak
  window.location.href = authUrl.toString();
}

/**
 * Échange le code d'autorisation contre un access token
 */
export async function exchangeCodeForTokens(code: string): Promise<TokenResponse> {
  // Récupérer le code verifier
  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  if (!codeVerifier) {
    throw new Error('Code verifier not found. PKCE flow compromised.');
  }

  // Préparer la requête
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    code: code,
    redirect_uri: REDIRECT_URI,
    code_verifier: codeVerifier,
  });

  try {
    const response = await fetch(TOKEN_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || 'Failed to exchange code for tokens');
    }

    const tokens: TokenResponse = await response.json();

    // Nettoyer le code verifier
    sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);

    // Stocker les tokens
    storeTokens(tokens);

    return tokens;
  } catch (error) {
    console.error('Token exchange failed:', error);
    throw error;
  }
}

/**
 * Rafraîchit l'access token avec le refresh token
 */
export async function refreshAccessToken(): Promise<TokenResponse | null> {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) {
    console.warn('No refresh token available');
    return null;
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: CLIENT_ID,
    refresh_token: refreshToken,
  });

  try {
    const response = await fetch(TOKEN_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      console.warn('Failed to refresh token, user needs to re-login');
      clearTokens();
      return null;
    }

    const tokens: TokenResponse = await response.json();

    // Stocker les nouveaux tokens
    storeTokens(tokens);

    return tokens;
  } catch (error) {
    console.error('Token refresh failed:', error);
    clearTokens();
    return null;
  }
}

/**
 * Force la déconnexion (tokens + session SSO Keycloak) puis redirige vers un
 * chemin local donné. Utilisé pour rejeter un utilisateur non autorisé
 * (ex : rôle VIEWER) dès le callback, sans laisser de session résiduelle.
 */
export function forceLogoutRedirect(postLogoutPath: string): void {
  const idToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
  clearTokens();
  const target = window.location.origin + postLogoutPath;
  if (idToken) {
    const url = new URL(LOGOUT_ENDPOINT);
    url.searchParams.set('id_token_hint', idToken);
    url.searchParams.set('post_logout_redirect_uri', target);
    window.location.href = url.toString();
  } else {
    window.location.href = target;
  }
}

/**
 * Déconnecte l'utilisateur de Keycloak
 */
export async function logout(): Promise<void> {
  const idToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

  // Nettoyer le stockage local
  clearTokens();

  // Révoquer le token côté Keycloak (best effort)
  if (refreshToken) {
    try {
      const body = new URLSearchParams({
        client_id: CLIENT_ID,
        refresh_token: refreshToken,
      });

      await fetch(LOGOUT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });
    } catch (error) {
      console.error('Logout revocation failed (non-critical):', error);
    }
  }

  // Rediriger vers la page de logout Keycloak (optionnel)
  if (idToken) {
    const logoutUrl = new URL(LOGOUT_ENDPOINT);
    logoutUrl.searchParams.set('id_token_hint', idToken);
    logoutUrl.searchParams.set('post_logout_redirect_uri', window.location.origin + '/login');

    window.location.href = logoutUrl.toString();
  }
}

/**
 * Récupère l'access token actuel (avec refresh automatique si nécessaire)
 */
export async function getAccessToken(): Promise<string | null> {
  let token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  if (!token) {
    return null;
  }

  // Vérifier si le token est expiré
  if (isTokenExpired(token)) {
    console.log('Access token expired, attempting refresh...');
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      token = refreshed.access_token;
    } else {
      return null;
    }
  }

  return token;
}

/**
 * Récupère les informations utilisateur depuis le token
 */
export function getUserFromToken(): KeycloakUser | null {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) {
    return null;
  }

  const userInfo = extractUserFromToken(token);
  if (!userInfo) {
    return null;
  }

  return {
    id: userInfo.id,
    username: userInfo.username,
    email: userInfo.email,
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    roles: userInfo.roles,
    emailVerified: userInfo.emailVerified,
  };
}

/**
 * Vérifie si l'utilisateur est authentifié
 */
export async function isAuthenticated(): Promise<boolean> {
  const token = await getAccessToken();
  return token !== null;
}

/**
 * Connexion via formulaire dédié (Resource Owner Password Credentials).
 * Envoie username/password directement au token endpoint Keycloak
 * (client public backoffice-spa, directAccessGrants activé, webOrigins *).
 * Lève une Error avec un message lisible en cas d'identifiants invalides.
 */
export async function loginWithPassword(username: string, password: string): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: CLIENT_ID,
    username,
    password,
    scope: 'openid profile email roles',
  });

  const response = await fetch(TOKEN_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    let msg = 'Identifiants invalides';
    if (response.status === 401) {
      msg = 'Nom d\'utilisateur ou mot de passe incorrect';
    }
    try {
      const err = await response.json();
      if (err.error_description) msg = err.error_description;
    } catch {
      /* corps non JSON */
    }
    throw new Error(msg);
  }

  const tokens: TokenResponse = await response.json();
  storeTokens(tokens);
  return tokens;
}

// ============================================================================
// Fonctions utilitaires internes
// ============================================================================

/**
 * Stocke les tokens dans localStorage
 */
function storeTokens(tokens: TokenResponse): void {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);

  if (tokens.refresh_token) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
  }

  if (tokens.id_token) {
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
  }

  // Extraire et stocker les informations utilisateur
  const user = extractUserFromToken(tokens.access_token);
  if (user) {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }
}

/**
 * Nettoie tous les tokens du localStorage
 */
export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
}

/**
 * Exporte la configuration pour debug
 */
export function getKeycloakConfig() {
  return {
    url: KEYCLOAK_URL,
    realm: REALM,
    clientId: CLIENT_ID,
    redirectUri: REDIRECT_URI,
    endpoints: {
      auth: AUTH_ENDPOINT,
      token: TOKEN_ENDPOINT,
      logout: LOGOUT_ENDPOINT,
    },
  };
}
